"use client";

import { createContext, useContext, type ReactNode } from "react";
import { useUser } from "../hooks/useUser";

type CurrentUserContextValue = ReturnType<typeof useUser>;

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

/**
 * Fetches the authenticated user exactly once (via useUser()) and shares it
 * through context. Mounted once at the root layout — consumers must use
 * useCurrentUser() instead of calling useUser() directly, so every page
 * doesn't trigger its own independent /api/me request.
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const value = useUser();

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser(): CurrentUserContextValue {
  const ctx = useContext(CurrentUserContext);

  if (!ctx) {
    throw new Error("useCurrentUser must be used within a CurrentUserProvider");
  }

  return ctx;
}

/**
 * The current user for consumers that legitimately render outside the provider
 * and only want the identity when there happens to be one — useSiteSettings,
 * whose consumers include RegionThemeSync, which the root layout mounts above
 * CurrentUserProvider.
 *
 * Returns null instead of throwing. Use useCurrentUser() anywhere the provider
 * is genuinely required, so a missing provider stays a loud bug there.
 */
export function useOptionalCurrentUser(): CurrentUserContextValue | null {
  return useContext(CurrentUserContext);
}
